import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import { toast } from 'react-toastify';
import { apiCall, API_BASE_URL, getAudiencesList, bulkCreditByAudience } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const getPromoCredits = (filters = {}) => {
  const q = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
  return apiCall(`${API_BASE_URL}/promo-wallet/credits${q.toString() ? '?' + q.toString() : ''}`);
};

const issuePromoCredit = (data) => apiCall(`${API_BASE_URL}/promo-wallet/issue`, { method: 'POST', body: JSON.stringify(data) });

const getConsumptionsByCredit = (creditId) => apiCall(`${API_BASE_URL}/promo-wallet/consumptions/credit/${creditId}`);

const PromoWallet = ({ embedded = false, leftSlot = null, scrollHeight = null } = {}) => {
  const isMobile = useIsMobile();
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_id: '', phone: '', status: '', source_type: '' });
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueData, setIssueData] = useState({ phone: '', amount: '', expiryDays: '30', sourceType: 'MANUAL', sourceRefId: '', category: '' });
  const [issuing, setIssuing] = useState(false);
  const [showConsumptionsModal, setShowConsumptionsModal] = useState(false);
  const [consumptions, setConsumptions] = useState([]);
  const [loadingConsumptions, setLoadingConsumptions] = useState(false);
  const [selectedCreditId, setSelectedCreditId] = useState(null);
  const [viewMode, setViewMode] = useState('entries');

  // Bulk Credit by Audience state
  const [showBulkCreditModal, setShowBulkCreditModal] = useState(false);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [bulkCreditData, setBulkCreditData] = useState({ audience_id: '', amount: '', expiryDays: '30', sourceType: 'AUDIENCE_CREDIT' });
  const [bulkCrediting, setBulkCrediting] = useState(false);
  const [bulkCreditResult, setBulkCreditResult] = useState(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  useEffect(() => { loadCredits(); }, []);

  const loadCredits = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getPromoCredits(f);
      setCredits(res?.data || []);
    } catch (err) { toast.error('Failed to load promo credits'); }
    finally { setLoading(false); }
  };

  const handleIssue = async () => {
    if (!issueData.phone || !issueData.amount || !issueData.sourceType) {
      toast.error('Phone number, amount, and source type are required');
      return;
    }
    if (!/^\d{10}$/.test(issueData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setIssuing(true);
    try {
      await issuePromoCredit({
        phone: issueData.phone,
        amount: parseFloat(issueData.amount),
        expiryDays: parseInt(issueData.expiryDays) || 30,
        sourceType: issueData.sourceType,
        sourceRefId: issueData.sourceRefId || null,
        category: issueData.category || null
      });
      toast.success('Promo credit issued!');
      setShowIssueModal(false);
      setIssueData({ phone: '', amount: '', expiryDays: '30', sourceType: 'MANUAL', sourceRefId: '', category: '' });
      loadCredits();
    } catch (err) { toast.error(err.message || 'Failed to issue credit'); }
    finally { setIssuing(false); }
  };

  const handleViewConsumptions = async (creditId) => {
    setSelectedCreditId(creditId);
    setShowConsumptionsModal(true);
    setLoadingConsumptions(true);
    try {
      const res = await getConsumptionsByCredit(creditId);
      setConsumptions(res?.data || []);
    } catch (err) { toast.error('Failed to load consumptions'); }
    finally { setLoadingConsumptions(false); }
  };

  const handleOpenBulkCredit = async () => {
    setShowBulkCreditModal(true);
    setBulkCreditResult(null);
    setLoadingAudiences(true);
    try {
      const res = await getAudiencesList({ status: 'active' });
      const audiences = res?.data?.audiences || res?.data || [];
      setAudienceOptions(audiences.map(a => ({ id: a.id, name: a.name, member_count: a.member_count })));
    } catch (err) { toast.error('Failed to load audiences'); }
    finally { setLoadingAudiences(false); }
  };

  const handleBulkCreditSubmit = () => {
    if (!bulkCreditData.audience_id) { toast.error('Please select an audience'); return; }
    if (!bulkCreditData.amount || parseFloat(bulkCreditData.amount) <= 0) { toast.error('Please enter a valid amount'); return; }
    setShowBulkConfirm(true);
  };

  const handleBulkCreditConfirm = async () => {
    setShowBulkConfirm(false);
    setBulkCrediting(true);
    try {
      const res = await bulkCreditByAudience({
        audience_id: parseInt(bulkCreditData.audience_id),
        amount: parseFloat(bulkCreditData.amount),
        expiryDays: parseInt(bulkCreditData.expiryDays) || 30,
        sourceType: bulkCreditData.sourceType,
      });
      setBulkCreditResult(res.data || res);
      toast.success(`Bulk credit completed! ${res.data?.credited || res.credited || 0} users credited.`);
      loadCredits();
    } catch (err) { toast.error(err.message || 'Failed to issue bulk credit'); }
    finally { setBulkCrediting(false); }
  };

  const selectedAudienceInfo = audienceOptions.find(a => String(a.id) === String(bulkCreditData.audience_id));

  const getStatusBadge = (status) => {
    const map = { ACTIVE: '#28a745', EXPIRED: '#6c757d', FULLY_USED: '#0d6efd' };
    return <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '4px', backgroundColor: map[status] || '#6c757d', color: '#fff' }}>{status}</span>;
  };

  const formatAmount = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(Number.parseFloat(value) || 0);

  const summary = useMemo(() => credits.reduce((totals, credit) => ({
    credit: totals.credit + (Number.parseFloat(credit.credit_amount) || 0),
    used: totals.used + (Number.parseFloat(credit.used_amount) || 0),
    remaining: totals.remaining + (Number.parseFloat(credit.remaining_amount) || 0),
  }), { credit: 0, used: 0, remaining: 0 }), [credits]);

  const userRows = useMemo(() => {
    const grouped = new Map();

    credits.forEach((credit) => {
      const userId = credit.user_id;
      const current = grouped.get(String(userId)) || {
        user_id: userId,
        name: credit.user?.name || '',
        phone: credit.user?.phone || '',
        entry_count: 0,
        credit_amount: 0,
        used_amount: 0,
        remaining_amount: 0,
      };

      current.entry_count += 1;
      current.credit_amount += Number.parseFloat(credit.credit_amount) || 0;
      current.used_amount += Number.parseFloat(credit.used_amount) || 0;
      current.remaining_amount += Number.parseFloat(credit.remaining_amount) || 0;
      grouped.set(String(userId), current);
    });

    return [...grouped.values()].sort((left, right) => (
      right.remaining_amount - left.remaining_amount || Number(left.user_id) - Number(right.user_id)
    ));
  }, [credits]);

  const entryColumns = [
    { name: 'User ID', selector: r => r.user_id, sortable: true, width: '80px' },
    { name: 'ID', selector: r => r.id, sortable: true, width: '60px' },
    { name: 'Credit', selector: r => r.credit_amount, sortable: true, width: '90px', cell: r => <span style={{ fontWeight: '600' }}>₹{parseFloat(r.credit_amount).toFixed(2)}</span> },
    { name: 'Used', selector: r => r.used_amount, sortable: true, width: '80px', cell: r => <span style={{ color: '#dc3545' }}>₹{parseFloat(r.used_amount).toFixed(2)}</span> },
    { name: 'Remaining', selector: r => r.remaining_amount, sortable: true, width: '100px', cell: r => <span style={{ fontWeight: '700', color: '#198754' }}>₹{parseFloat(r.remaining_amount).toFixed(2)}</span> },
    { name: 'Status', selector: r => r.status, sortable: true, width: '110px', cell: r => getStatusBadge(r.status) },
    { name: 'Source', selector: r => r.source_type, sortable: true, width: '140px', cell: r => <span style={{ fontSize: '11px' }}>{r.source_type}</span> },
    { name: 'Ref', selector: r => r.source_ref_id, width: '120px', cell: r => <span style={{ fontSize: '11px' }}>{r.source_ref_id || '—'}</span> },
    { name: 'Category', selector: r => r.category, width: '100px', cell: r => <span style={{ fontSize: '11px' }}>{r.category || '—'}</span> },
    { name: 'Expiry', selector: r => r.expiry_at, sortable: true, width: '110px', cell: r => {
      const d = new Date(r.expiry_at);
      const expired = d < new Date();
      return <span style={{ fontSize: '11px', color: expired ? '#dc3545' : '#212529' }}>{d.toLocaleDateString('en-IN')}</span>;
    }},
    { name: 'Created', selector: r => r.createdAt, sortable: true, width: '110px', cell: r => <span style={{ fontSize: '11px' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—'}</span> },
    { name: 'Usage', width: '80px', cell: r => r.used_amount > 0 ? (
      <button onClick={() => handleViewConsumptions(r.id)} style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 12px' : '2px 8px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>View</button>
    ) : <span style={{ fontSize: '11px', color: '#6c757d' }}>—</span> },
  ];

  const userColumns = [
    { name: 'User ID', selector: r => r.user_id, sortable: true, width: '100px' },
    {
      name: 'User',
      selector: r => r.name || r.phone || 'N/A',
      sortable: true,
      grow: 1.4,
      cell: r => (
        <div style={{ minWidth: 0, padding: '6px 0' }}>
          <div style={{ color: '#1f2937', fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.name || 'Name not available'}
          </div>
          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{r.phone || 'Phone not available'}</div>
        </div>
      ),
    },
    { name: 'Credit Entries', selector: r => r.entry_count, sortable: true, width: '120px' },
    {
      name: 'Total Credit',
      selector: r => r.credit_amount,
      sortable: true,
      width: '150px',
      cell: r => <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{formatAmount(r.credit_amount)}</span>,
    },
    {
      name: 'Used',
      selector: r => r.used_amount,
      sortable: true,
      width: '150px',
      cell: r => <span style={{ color: '#dc2626', fontWeight: 650 }}>{formatAmount(r.used_amount)}</span>,
    },
    {
      name: 'Remaining',
      selector: r => r.remaining_amount,
      sortable: true,
      width: '160px',
      cell: r => <span style={{ color: '#15803d', fontWeight: 750 }}>{formatAmount(r.remaining_amount)}</span>,
    },
  ];

  // Filter-bar styling — flat & minimal, in sync with the Ledger filter bar.
  const filterBarStyle = {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
    padding: '8px 0', borderBottom: '1px solid #eef1f5',
  };
  const fInput = { height: '32px', fontSize: '13px', padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', color: '#1f2937', background: '#fff' };
  const fBtn = { height: '32px', fontSize: '12px', fontWeight: 600, padding: '0 14px', borderRadius: '8px', flexShrink: 0 };
  const vDivider = { width: '1px', height: '20px', background: '#e2e8f0', flexShrink: 0, margin: '0 2px' };

  const overviewCards = [
    { label: 'Total Credit', value: summary.credit, accent: '#64748b' },
    { label: 'Used', value: summary.used, accent: '#a16207' },
    { label: 'Remaining', value: summary.remaining, accent: '#047857' },
  ];

  const viewToggle = (
    <div role="group" aria-label="Promo wallet view" style={{ display: 'inline-flex', padding: '2px', border: '1px solid #e2e8f0', borderRadius: '9px', background: '#f1f5f9', flexShrink: 0 }}>
      {[
        { value: 'entries', label: 'All entries' },
        { value: 'users', label: 'User-wise' },
      ].map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => setViewMode(option.value)}
          aria-pressed={viewMode === option.value}
          style={{
            height: '28px',
            padding: '0 13px',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            color: viewMode === option.value ? '#334155' : '#7b8799',
            background: viewMode === option.value ? '#fff' : 'transparent',
            boxShadow: viewMode === option.value ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
            fontSize: '11px',
            fontWeight: viewMode === option.value ? 700 : 600,
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const overview = (
    <section style={{ marginBottom: '10px', padding: '10px 12px', border: '1px solid #e5eaf1', borderRadius: '11px', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#334155', fontSize: '12px', fontWeight: 700 }}>Promo wallet overview</div>
          <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '1px' }}>
            {credits.length.toLocaleString('en-IN')} entries · {userRows.length.toLocaleString('en-IN')} users
          </div>
        </div>
        {viewToggle}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
        {overviewCards.map(card => (
          <div key={card.label} style={{ minWidth: 0, padding: '9px 11px', border: '1px solid #e2e8f0', borderLeft: `3px solid ${card.accent}`, borderRadius: '9px', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#7b8799', fontSize: '9px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: card.accent, flexShrink: 0 }} />
              {card.label}
            </div>
            <div style={{ color: '#253247', fontSize: isMobile ? '16px' : '17px', fontWeight: 700, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatAmount(card.value)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  // Flat, sleek filter + actions toolbar (no nested boxes, no icons).
  const toolbar = (
    <div style={{ ...filterBarStyle, ...(embedded ? { flexWrap: 'nowrap', overflowX: 'auto' } : {}) }}>
      {leftSlot}
      {leftSlot && <span style={vDivider} />}

      <input type="text" placeholder="User ID" value={filters.user_id} onChange={e => setFilters(p => ({ ...p, user_id: e.target.value }))}
        style={{ ...fInput, width: '90px' }} />
      <input type="tel" placeholder="Phone" value={filters.phone} onChange={e => setFilters(p => ({ ...p, phone: e.target.value }))}
        style={{ ...fInput, width: '130px' }} />
      <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}
        style={{ ...fInput, cursor: 'pointer' }}>
        <option value="">All Status</option>
        <option value="ACTIVE">Active</option>
        <option value="EXPIRED">Expired</option>
        <option value="FULLY_USED">Fully Used</option>
      </select>
      <select value={filters.source_type} onChange={e => setFilters(p => ({ ...p, source_type: e.target.value }))}
        style={{ ...fInput, cursor: 'pointer' }}>
        <option value="">All Sources</option>
        <option value="ORDER_CASHBACK">Order Cashback</option>
        <option value="REFERRAL">Referral</option>
        <option value="MANUAL">Manual</option>
        <option value="SCRATCH_CARD">Scratch Card</option>
      </select>

      <button onClick={() => loadCredits(filters)} className="btn btn-primary" style={fBtn}>Search</button>
      <button onClick={() => { setFilters({ user_id: '', phone: '', status: '', source_type: '' }); loadCredits({}); }} className="btn btn-secondary" style={fBtn}>Clear</button>

      <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
        <button onClick={handleOpenBulkCredit} className="btn btn-primary" style={fBtn}>Bulk Credit</button>
        <button onClick={() => setShowIssueModal(true)} className="btn btn-success" style={fBtn}>+ Issue Credit</button>
      </div>
    </div>
  );

  const embeddedTableHeight = scrollHeight && String(scrollHeight).endsWith('px')
    ? `${Math.max(240, Number.parseFloat(scrollHeight) - (isMobile ? 285 : 160))}px`
    : 'calc(100vh - 350px)';

  const promoTable = (
    <DataTable key={viewMode} columns={viewMode === 'users' ? userColumns : entryColumns} data={viewMode === 'users' ? userRows : credits} progressPending={loading} pagination paginationPerPage={25}
      fixedHeader fixedHeaderScrollHeight={embedded ? embeddedTableHeight : '60vh'}
      customStyles={{ headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' } }, headCells: { style: { fontWeight: '600', color: '#495057', fontSize: '11px' } } }}
      noDataComponent={<div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>{viewMode === 'users' ? 'No users found' : 'No promo credits found'}</div>}
      dense />
  );

  const modals = (
    <>
      {/* Issue Modal */}
      {showIssueModal && (
        <div onClick={() => setShowIssueModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>Issue Promo Credit</span>
              <button onClick={() => setShowIssueModal(false)} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>
            <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Phone Number *', key: 'phone', type: 'text', placeholder: '10-digit phone number' },
                { label: 'Amount (₹) *', key: 'amount', type: 'number', placeholder: 'e.g. 200' },
                { label: 'Expiry Days', key: 'expiryDays', type: 'number', placeholder: '30' },
                { label: 'Source Ref ID', key: 'sourceRefId', type: 'text', placeholder: 'Order ID or ref' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>{f.label}</label>
                  <input type={f.type} value={issueData[f.key]} onChange={e => setIssueData(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} maxLength={f.key === 'phone' ? 10 : undefined}
                    inputMode={f.key === 'phone' ? 'tel' : f.key === 'amount' ? 'decimal' : undefined}
                    style={{ width: '100%', fontSize: '13px', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>Category</label>
                <select value={issueData.category} onChange={e => setIssueData(p => ({ ...p, category: e.target.value }))}
                  style={{ width: '100%', fontSize: '13px', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                  <option value="">All Categories (No restriction)</option>
                  <option value="1">Solar Panels</option>
                  <option value="2">Inverters</option>
                  <option value="3">BOS</option>
                  <option value="4">Kit</option>
                  <option value="5">Battery</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>Source Type *</label>
                <select value={issueData.sourceType} onChange={e => setIssueData(p => ({ ...p, sourceType: e.target.value }))}
                  style={{ width: '100%', fontSize: '13px', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                  <option value="MANUAL">Manual</option>
                  <option value="ORDER_CASHBACK">Order Cashback</option>
                  <option value="REFERRAL">Referral</option>
                  <option value="SCRATCH_CARD">Scratch Card</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button onClick={() => setShowIssueModal(false)} style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleIssue} disabled={issuing} style={{ fontSize: '13px', padding: '7px 16px', background: '#28a745', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: issuing ? 0.7 : 1 }}>
                {issuing ? 'Issuing...' : 'Issue Credit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consumptions Modal */}
      {showConsumptionsModal && (
        <div onClick={() => setShowConsumptionsModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '500px', maxHeight: '70vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '700' }}>Usage History — Credit #{selectedCreditId}</span>
              <button onClick={() => setShowConsumptionsModal(false)} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>
            <div style={{ padding: '18px' }}>
              {loadingConsumptions ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>Loading...</div>
              ) : consumptions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>No usage records found</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Order ID</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Amount Used</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumptions.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px' }}>
                          <a href={`/orders/${c.order_id}`} target={isMobile ? undefined : '_blank'} rel="noreferrer" style={{ color: '#0d6efd', fontWeight: '600' }}>{c.order_id}</a>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>₹{parseFloat(c.used_amount).toFixed(2)}</td>
                        <td style={{ padding: '8px', color: '#6c757d' }}>{new Date(c.created_at).toLocaleDateString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Credit by Audience Modal */}
      {showBulkCreditModal && (
        <div onClick={() => { setShowBulkCreditModal(false); setBulkCreditResult(null); setBulkCreditData({ audience_id: '', amount: '', expiryDays: '30', sourceType: 'AUDIENCE_CREDIT' }); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#eff6ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#1d4ed8' }}>🎯 Bulk Credit by Audience</span>
              <button onClick={() => { setShowBulkCreditModal(false); setBulkCreditResult(null); setBulkCreditData({ audience_id: '', amount: '', expiryDays: '30', sourceType: 'AUDIENCE_CREDIT' }); }} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>
            <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Audience Dropdown */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Target Audience *</label>
                {loadingAudiences ? (
                  <div style={{ fontSize: '13px', color: '#6c757d', padding: '8px' }}>Loading audiences...</div>
                ) : (
                  <select
                    value={bulkCreditData.audience_id}
                    onChange={e => setBulkCreditData(p => ({ ...p, audience_id: e.target.value }))}
                    style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  >
                    <option value="">Select an audience...</option>
                    {audienceOptions.map(a => (
                      <option key={a.id} value={a.id}>
                        AUD-{a.id} — {a.name} ({a.member_count?.toLocaleString()} members)
                      </option>
                    ))}
                  </select>
                )}
                {selectedAudienceInfo && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#2563eb', fontWeight: '600' }}>
                    ✓ {selectedAudienceInfo.member_count?.toLocaleString()} members will be credited
                  </div>
                )}
              </div>

              {/* Amount */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Amount per User (₹) *</label>
                <input
                  type="number"
                  value={bulkCreditData.amount}
                  onChange={e => setBulkCreditData(p => ({ ...p, amount: e.target.value }))}
                  placeholder="e.g. 100"
                  inputMode="decimal"
                  min="1"
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Expiry Days */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Expiry (days)</label>
                <input
                  type="number"
                  value={bulkCreditData.expiryDays}
                  onChange={e => setBulkCreditData(p => ({ ...p, expiryDays: e.target.value }))}
                  placeholder="30"
                  min="1"
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Source Type */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Source Type</label>
                <select
                  value={bulkCreditData.sourceType}
                  onChange={e => setBulkCreditData(p => ({ ...p, sourceType: e.target.value }))}
                  style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                >
                  <option value="AUDIENCE_CREDIT">Audience Credit</option>
                  <option value="MANUAL">Manual</option>
                  <option value="ORDER_CASHBACK">Order Cashback</option>
                </select>
              </div>

              {/* Result display */}
              {bulkCreditResult && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>✅ Bulk Credit Complete</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px', fontSize: '12px', color: '#334155' }}>
                    <div>Audience: <strong>AUD-{bulkCreditResult.audience_id}</strong></div>
                    <div>Total Members: <strong>{bulkCreditResult.total_members}</strong></div>
                    <div>Credited: <strong style={{ color: '#16a34a' }}>{bulkCreditResult.credited}</strong></div>
                    <div>Failed: <strong style={{ color: bulkCreditResult.failed > 0 ? '#dc2626' : '#6c757d' }}>{bulkCreditResult.failed}</strong></div>
                    <div>Amount/User: <strong>₹{bulkCreditResult.amount_per_user}</strong></div>
                    <div>Expires in: <strong>{bulkCreditResult.expiry_days} days</strong></div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button onClick={() => { setShowBulkCreditModal(false); setBulkCreditResult(null); setBulkCreditData({ audience_id: '', amount: '', expiryDays: '30', sourceType: 'AUDIENCE_CREDIT' }); }} style={{ fontSize: '13px', padding: '7px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>
                {bulkCreditResult ? 'Close' : 'Cancel'}
              </button>
              {!bulkCreditResult && (
                <button onClick={handleBulkCreditSubmit} disabled={bulkCrediting} style={{ fontSize: '13px', padding: '7px 16px', background: '#2563eb', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600', opacity: bulkCrediting ? 0.7 : 1 }}>
                  {bulkCrediting ? 'Processing...' : 'Credit Audience'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Credit Confirmation Dialog */}
      {showBulkConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '420px', maxWidth: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#fef3c7', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>💰</div>
              <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Confirm Bulk Credit</h4>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '16px' }}>
              This will credit <strong>₹{bulkCreditData.amount}</strong> to{' '}
              <strong>{selectedAudienceInfo?.member_count?.toLocaleString() || '?'}</strong> users in{' '}
              <strong>AUD-{bulkCreditData.audience_id}</strong>.
            </p>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
              Total value: ₹{((parseFloat(bulkCreditData.amount) || 0) * (selectedAudienceInfo?.member_count || 0)).toLocaleString()}. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowBulkConfirm(false)} style={{ fontSize: '13px', padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleBulkCreditConfirm} style={{ fontSize: '13px', padding: '8px 16px', background: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600' }}>Yes, Credit All</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Embedded (inside the Ledger view's Promo Wallet tab): no outer container,
  // just the compact toolbar + scrolling table. The parent provides the 15/85
  // frame. Standalone (/promo-wallet route): keep the original container/card.
  if (embedded) {
    return (
      <>
        <div style={{ marginBottom: '10px' }}>{toolbar}</div>
        {overview}
        {promoTable}
        {modals}
      </>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '12px' }}>{toolbar}</div>
        {overview}
        {promoTable}
      </div>
      {modals}
    </div>
  );
};

export default PromoWallet;
